import { Box, Dialog, Typography } from "@material-ui/core";
import React from "react";
import { useTranslation } from "react-i18next";
import MoveEntitiesReviewPanel from "../../../../../ui/components/MoveEntitiesReviewPanel/MoveEntitiesReviewPanel";
import SearchDestinationPanel from "../../../../../ui/components/SearchDestinationPanel/SearchDestinationPanel";
import { DEFAULT_SPACING } from "../../../../../ui/theme/dimensions";
import { formatPropertyAddress } from "../../../../../utils/formatAddress";
import TMoveDestination from "../../../../admin/AdminMoveEntity/types/TMoveDestination";
import TSelectedMoveTab from "../../../../admin/AdminMoveEntity/types/TMoveTab";
import ProCheckResult from "../../../../../rest/procheck/model/ProCheckResult";
import MoveEntityWarningPanel from "../../../../../ui/components/MoveEntityWarningPanel/MoveEntityWarningPanel";
import MoveEntitySuccessPanel from "../../../../../ui/components/MoveEntitySuccessPanel/MoveEntitySuccessPanel";
import { IMoveActionResult } from "../../../../admin/AdminMoveEntity/services/AdminMoveEntityService";
import MoveEntityErrorPanel from "../../../../../ui/components/MoveEntityErrorPanel/MoveEntityErrorPanel";

interface IProps {
  open: boolean;
  handleOpen: () => void;
  handleSearchPressed: () => void;
  handleSetSearchString: (v: string) => void;
  setSelectedDestination: (id: string) => void;
  searchValue: string;
  isLoading: boolean | undefined;
  moved: boolean | undefined;
  destinations: TMoveDestination[];
  moveResults?: IMoveActionResult;
  handleNextPressed: () => void;
  handleBackPressed: () => void;
  handleMovePressed: () => void;
  handleSuccessPressed: () => void;
  activeTab: TSelectedMoveTab;
  selectedDestination?: string;
  selectedTests: ProCheckResult[];
}

const MoveProcheckReportDialog = ({
  open,
  handleOpen,
  searchValue,
  isLoading,
  handleSearchPressed,
  handleSetSearchString,
  destinations,
  selectedDestination,
  setSelectedDestination,
  handleNextPressed,
  handleBackPressed,
  handleMovePressed,
  handleSuccessPressed,
  activeTab,
  selectedTests,
  moveResults,
}: IProps) => {
  //Hooks
  const { t } = useTranslation();

  //Strings
  const searchPlaceholder = t("moveProCheckDialog.searchPlaceholder");

  const destination = destinations.filter((dest) => dest.id === selectedDestination)[0];

  const getSuccessItems = () => {
    return selectedTests.filter((test) => {
      return moveResults?.movedEntities.find((id) => id === test.id) !== undefined;
    });
  };

  const getFailureItems = () => {
    return selectedTests.filter((test) => {
      return moveResults?.rejectedEntities.find((id) => id === test.id) !== undefined;
    });
  };

  const getTitle = () => {
    switch (activeTab) {
      case "destination":
        return t("moveProCheckDialog.destinationTitle");
      case "confirmation":
        return t("moveProCheckDialog.title", { count: selectedTests.length });
      case "warning":
        return t("moveProCheckDialog.confirmationTitle");
      case "success":
        return t("moveProCheckDialog.successTitle", { count: selectedTests.length });
      case "failure":
        return t("moveProCheckDialog.failureTitle", { count: selectedTests.length });
    }
  };

  //Renderers
  const renderBody = () => {
    switch (activeTab) {
      case "destination":
        return (
          <SearchDestinationPanel
            searchValue={searchValue}
            searchPlaceholder={searchPlaceholder}
            handleSearchChange={handleSetSearchString}
            handleSearchButtonClicked={handleSearchPressed}
            handleBackClicked={handleOpen}
            handleNextClicked={handleNextPressed}
            handleSelectDestination={setSelectedDestination}
            destinations={destinations}
            selectedDestinationId={selectedDestination}
            isLoading={isLoading}
          />
        );
      case "confirmation":
        return (
          <MoveEntitiesReviewPanel
            destination={destination}
            entityType={"procheck"}
            selectedItems={selectedTests}
            getSelectedItemName={(item) =>
              `ProCheck, ${formatPropertyAddress(item.property, t)}`
            }
            handleBackPressed={handleBackPressed}
            handleNextPressed={handleNextPressed}
          />
        );
      case "warning":
        return (
          <MoveEntityWarningPanel
            destination={destination}
            loading={isLoading}
            getOrigin={() => `${formatPropertyAddress(selectedTests[0].property, t)}`}
            entityType={"procheck"}
            selectedItems={selectedTests}
            getTypeName={() => "ProCheck"}
            getSelectedItemName={(item) => item.tested_at}
            handleBackPressed={handleBackPressed}
            handleNextPressed={handleMovePressed}
          />
        );
      case "success":
        return (
          <MoveEntitySuccessPanel
            entityType={"procheck"}
            moveResults={moveResults}
            getItemName={(item) => `ProCheck ${item.tested_at}`}
            destination={destination}
            items={getSuccessItems()}
            rejectedItems={getFailureItems()}
            handleClosePressed={handleSuccessPressed}
          />
        );
      case "failure":
        return (
          <MoveEntityErrorPanel
            entityType={"procheck"}
            getItemName={(item) => `ProCheck ${item.tested_at}`}
            destination={destination}
            items={getFailureItems()}
            handleClosePressed={handleOpen}
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={handleOpen}>
      <Box
        display="flex"
        flexDirection="column"
        width={500}
        paddingX={DEFAULT_SPACING * 1.5}
        paddingBottom={DEFAULT_SPACING}
        paddingTop={DEFAULT_SPACING * 1.5}
      >
        <Typography
          align="center"
          style={{ textTransform: "uppercase" }}
          variant="h2"
          gutterBottom
        >
          <strong>{getTitle()}</strong>
        </Typography>
        {renderBody()}
      </Box>
    </Dialog>
  );
};

export default MoveProcheckReportDialog;
